import { rem } from "@certn/cascadia";
import {
  lineHeights,
  sizes,
  textStyles,
  weights,
} from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import { RE_DIGIT } from "./constants";
import { useColors } from "../../../hooks/useColors";
import { useGetAllFocusShadows } from "../../utils";
import { FormFeedback } from "../_assets/input-wrapper/FormFeedback";

export type CodeInputProps = {
  value: string;
  valueLength: number;
  isSuccess: boolean;
  isFail: boolean;
  isDisabled?: boolean;
  onChange: (value: string) => void;
  testID?: string;
};

export function CodeInput({
  value,
  valueLength,
  onChange,
  isSuccess,
  isFail,
  isDisabled,
  testID,
}: CodeInputProps) {
  const values = textStyles["input/text-field-code"];
  const colors = useColors();
  const borderColor = isDisabled
    ? "var(--border-input-disabled)"
    : isFail
      ? `${colors["border-danger-input"]}`
      : `${colors["border-input"]}`;
  const boxShadow = borderColor ? `inset 0 0 0 1px ${borderColor}` : undefined;
  const getAllFocusShadows = useGetAllFocusShadows();
  const { t } = useTranslation();
  const wrapperRef = useRef<HTMLDivElement>(null);

  const focusInput = (index: number) => {
    const input = wrapperRef.current?.children.item(index);
    if (input) {
      (input as HTMLInputElement).focus();
    }
  };

  useEffect(() => {
    focusInput(value.length);
  }, [value]);

  const inputOnKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    idx: number
  ) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      onChange(value.substring(0, idx - 1));
      return;
    }
    if (RE_DIGIT.test(e.key)) {
      if (idx < value.length) {
        onChange(value.substring(0, idx) + e.key + value.substring(idx + 1));
      } else {
        onChange(value + e.key);
      }
    }
  };
  const inputOnChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    idx: number
  ) => {
    e.target.value.trim();

    if (e.target.value.length === valueLength) {
      onChange(e.target.value);
      e.target.blur();
    }
  };

  const valueItems = useMemo(() => {
    const valueArray = value.split("");
    const items: string[] = [];

    for (let i = 0; i < valueLength; i++) {
      const char = valueArray[i];
      if (RE_DIGIT.test(char)) {
        items.push(char);
      } else {
        items.push("");
      }
    }
    return items;
  }, [value, valueLength]);

  useEffect(() => {
    focusInput(0);
  }, []);

  return (
    <>
      <div ref={wrapperRef} className={styles.container}>
        {valueItems.map((val, idx) => (
          <input
            key={idx}
            aria-label={`6-digit-code-entry-component-${idx}`}
            data-testid={`${testID}-${idx + 1}`}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="\d{1}"
            maxLength={valueLength}
            className={css(styles.input, {
              borderColor,
              color: isDisabled ? "var(--text-disabled)" : "var(--text-base)",
              fontSize: rem(sizes[values.size]),
              lineHeight: rem(lineHeights[values.lineHeight]),
              fontWeight: weights[values.weight],
              outline: "none",
              background: isDisabled ? undefined : "var(--background-input)",
              "&:hover": css({
                borderColor: isDisabled ? undefined : borderColor,
              }),
              "&:focus": css({
                boxShadow,
              }),
              "&:focus-within, &:focus-visible": css(
                getAllFocusShadows(boxShadow),
                {
                  boxShadow,
                  borderColor,
                }
              ),
            })}
            value={val}
            onClick={() => focusInput(value.length)}
            onChange={(e) => inputOnChange(e, idx)}
            onKeyDown={(e) => inputOnKeyDown(e, idx)}
            disabled={isDisabled || idx > value.length}
          />
        ))}
      </div>
      <div className={styles.container} style={{ justifyContent: "center" }}>
        {isSuccess && <FormFeedback type="valid">{t("Success")}</FormFeedback>}
        {isFail && <FormFeedback>{t("This code is not valid")}</FormFeedback>}
      </div>
    </>
  );
}

const styles = {
  container: css({
    display: "flex",
    width: "280px",
    marginBottom: "var(--space-2)",
    columnGap: "var(--space-2)",
  }),
  input: css({
    width: "100%",
    fontFamily: "var(--font-family-default)",
    paddingTop: "var(--space-2)",
    paddingRight: "var(--space-3)",
    paddingBottom: "var(--space-2)",
    paddingLeft: "var(--space-3)",
    border: "1px solid",
    borderRadius: "var(--border-radius-2)",
    textAlign: "center",
    alignItems: "center",
    outline: "none",
  }),
};
